import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useHttp from "../../../../hooks/use-http";
import convertToExcel from "../../../../components/ExportToExcel";
import Pagination from "../../../../components/Pagination";

const DeviceLockUnlockReport = () => {
  const navigate = useNavigate();

  const handleButtonClick = () => {
    navigate("/employeeMis");
  };

  /* ---------------- STATE ---------------- */
  const [data, setData] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);

  const [employeeId, setEmployeeId] = useState(0);

  const [showDateFilter, setShowDateFilter] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const currentDate = new Date().toISOString().slice(0, 10);

  /* ---------------- PAGINATION ---------------- */
  const itemsPerPage = localStorage.getItem("pageSize") || 10;
  const [currentPage, setCurrentPage] = useState(1);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = data.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  /* ---------------- API ---------------- */
  const { sendRequest: getDeviceUnlockData } = useHttp();

  /* ---------------- DEFAULT LOAD ---------------- */
  useEffect(() => {
    const url = `/api/deviceLockUnlockReport/?orgId=${localStorage.getItem(
      "orgId"
    )}`;

    getDeviceUnlockData({ url }, (res) => {
      if (res && res.data) {
        setData(res.data);

        // build employee list (ID + Name)
        const uniqueEmployees = [];
        const map = new Map();

        res.data.forEach((item) => {
          if (!map.has(item.employeeId)) {
            map.set(item.employeeId, true);
            uniqueEmployees.push({
              employeeId: item.employeeId,
              employeeName: item.employeeName,
            });
          }
        });

        setEmployeeList(uniqueEmployees);
      }
    });
  }, [getDeviceUnlockData]);

  /* ---------------- CLEAR TABLE ON FILTER CHANGE ---------------- */
  useEffect(() => {
    setData([]);
  }, [employeeId, showDateFilter, fromDate, toDate]);

  /* ---------------- SUBMIT ---------------- */
  const handleSubmit = () => {
    let urlfilter = "";

    if (employeeId > 0) urlfilter += `&employeeId=${employeeId}`;
    if (showDateFilter && fromDate && toDate) {
      urlfilter += `&fromDate=${fromDate}&toDate=${toDate}`;
    }

    const url = `/api/deviceLockUnlockReport/?orgId=${localStorage.getItem(
      "orgId"
    )}${urlfilter}`;

    getDeviceUnlockData({ url }, (res) => {
      if (res && res.data) {
        setData(res.data);
        setCurrentPage(1);
      }
    });
  };

  /* ---------------- DATE FILTER ---------------- */
  const handleDateFilterChange = (e) => {
    if (e.target.value === "Yes") {
      setShowDateFilter(true);
    } else {
      setShowDateFilter(false);
      setFromDate("");
      setToDate("");
    }
  };

  /* ---------------- EXCEL EXPORT ---------------- */
  const handleExportExcel = () => {
    const exportData = data.map((item, index) => ({
      srNo: index + 1,
      date: item.unlockDate,
      employeeName: item.employeeName,
      comment: item.comment,
      resetBy: item.createdByName,
    }));

    convertToExcel(
      exportData,
      ["srNo", "date", "employeeName", "comment", "resetBy"],
      "deviceLockUnlockReport.xlsx",
      ["Sr No", "Date", "Employee Name", "Comment", "Reset By"]
    );
  };

  /* ---------------- JSX ---------------- */
  return (
    <div className="content-wrapper">
      {/* HEADER */}
      <div className="row mx-2">
        <div className="col-md-8 mt-3">
          <label className="card-title mx-3">
            Device Lock Unlock Report
          </label>
        </div>
        <div className="col-md-4 text-right mt-3">
          <button className="btn btn-tool" onClick={handleButtonClick}>
            <i className="fas fa-times" />
          </button>
        </div>
      </div>

      <br />

      {/* FILTERS */}
      <section className="content">
        <div className="container-fluid">
          <div className="row">
            {/* EMPLOYEE */}
            <div className="col-md-3">
              <label>Employee Name</label>
              <select
                className="form-control"
                value={employeeId}
                onChange={(e) => setEmployeeId(Number(e.target.value))}
              >
                <option value={0}>Select</option>
                {employeeList.map((emp) => (
                  <option key={emp.employeeId} value={emp.employeeId}>
                    {emp.employeeName}
                  </option>
                ))}
              </select>
            </div>

            {/* DATE FILTER */}
            <div className="col-md-3">
              <label>Apply Date Filter</label>
              <select
                className="form-control"
                onChange={handleDateFilterChange}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            {showDateFilter && (
              <>
                <div className="col-md-3">
                  <label>From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    max={currentDate}
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>

                <div className="col-md-3">
                  <label>To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    max={currentDate}
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>
              </>
            )}

            {/* SUBMIT */}
            <div className="col-md-3 text-right">
              <label>&nbsp;</label>
              <button
                className="form-control btn btn-sm btn-primary"
                onClick={handleSubmit}
              >
                Submit
              </button>
            </div>
          </div>
        </div>

        <hr />

        {/* TABLE */}
        <div className="container-fluid">
          <div className="text-right my-2">
            <button
              className="btn btn-sm btn-warning"
              onClick={handleExportExcel}
              disabled={data.length === 0}
            >
              <i className="fas fa-file-excel" />
            </button>
          </div>

          <div className="card">
            <div className="card-body" style={{ padding: "0.4rem" }}>
              <table className="table table-bordered text-center table-sm">
                <thead>
                  <tr className="table-header">
                    <th>Sr No</th>
                    <th>Date</th>
                    <th>Employee Name</th>
                    <th>Comment</th>
                    <th>Reset By</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length === 0 ? (
                    <tr>
                      <td colSpan="5">No data found</td>
                    </tr>
                  ) : (
                    currentItems.map((item, index) => (
                      <tr key={index}>
                        <td>{indexOfFirstItem + index + 1}</td>
                        <td>{item.unlockDate}</td>
                        <td>{item.employeeName}</td>
                        <td>{item.comment}</td>
                        <td>{item.createdByName}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="card-footer clearfix">
              <Pagination
                totalItems={data.length}
                itemsPerPage={itemsPerPage}
                onPageChange={paginate}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DeviceLockUnlockReport;
